import type { Millis, Seconds } from './types';

export const CALIBRATION_KEY = 'solta.calibration.v1';
export const MAX_OFFSET_MS: Millis = 100;
export const WARMUP_TAPS = 2;
export const MIN_TAPS = 5;
export const OUTLIER_MS: Millis = 120;

export interface CalibrationResult {
  offsetMs: Millis;
  spreadMs: Millis;
  used: number;
  rejected: number;
}

export interface OffsetStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function clampOffset(value: Millis): Millis {
  if (!Number.isFinite(value)) return 0;
  if (value < -MAX_OFFSET_MS) return -MAX_OFFSET_MS;
  if (value > MAX_OFFSET_MS) return MAX_OFFSET_MS;
  return value;
}

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  const low = sorted[middle - 1];
  const high = sorted[middle];
  if (high === undefined) return 0;
  if (sorted.length % 2 === 1) return high;
  return low === undefined ? high : (low + high) / 2;
}

function spread(values: readonly number[], center: number): Millis {
  if (values.length < 2) return 0;
  const variance = values.reduce((sum, v) => sum + (v - center) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export function tapDeltaMs(tap: Seconds, gridStart: Seconds, periodSeconds: Seconds): Millis {
  if (!(periodSeconds > 0)) return 0;
  const raw = (tap - gridStart) / periodSeconds;
  const delta = (raw - Math.round(raw)) * periodSeconds;
  return Math.round(delta * 1e6) / 1e3;
}

export function computeOffset(samples: readonly Millis[]): CalibrationResult | null {
  const taps = samples.slice(WARMUP_TAPS).filter((value) => Number.isFinite(value));
  if (taps.length < MIN_TAPS) return null;
  const rough = median(taps);
  const kept = taps.filter((value) => Math.abs(value - rough) <= OUTLIER_MS);
  if (kept.length < MIN_TAPS) return null;
  const center = median(kept);
  return {
    offsetMs: clampOffset(Math.round(center * 10) / 10),
    spreadMs: Math.round(spread(kept, center) * 10) / 10,
    used: kept.length,
    rejected: taps.length - kept.length,
  };
}

export function mergeOffset(previousMs: Millis, measuredMs: Millis): Millis {
  return clampOffset(Math.round((previousMs + measuredMs) * 10) / 10);
}

function attempt<T>(action: () => T, fallback: T): T {
  try {
    return action();
  } catch {
    return fallback;
  }
}

export function loadOffsetMs(store: OffsetStore | null = safeStore()): Millis {
  const raw = attempt<string | null>(() => store?.getItem(CALIBRATION_KEY) ?? null, null);
  if (raw === null) return 0;
  return clampOffset(Number.parseFloat(raw));
}

export function saveOffsetMs(offsetMs: Millis, store: OffsetStore | null = safeStore()): Millis {
  const value = clampOffset(offsetMs);
  attempt<void>(() => {
    store?.setItem(CALIBRATION_KEY, String(value));
  }, undefined);
  return value;
}

export function clearOffset(store: OffsetStore | null = safeStore()): void {
  attempt<void>(() => {
    store?.removeItem(CALIBRATION_KEY);
  }, undefined);
}

function safeStore(): OffsetStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
